import axios, { AxiosInstance } from 'axios';

// Environment-based configuration
const API_BASE_URL = import.meta.env.VITE_FUNCTIONS_URL || 'http://localhost:7071';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor for debugging
    this.client.interceptors.request.use(
      (config) => {
        console.log('API Request:', config.method?.toUpperCase(), config.url);
        return config;
      },
      (error) => {
        console.error('Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => {
        console.log('API Response:', response.status, response.data);
        return response;
      },
      (error) => {
        console.error('Response Error:', error.response?.status, error.response?.data);
        return Promise.reject(error);
      }
    );
  }

  // Test connection to Azure Functions
  async testConnection(): Promise<string> {
    try {
      const response = await this.client.get('/api/echo');
      return response.data?.message || 'Connection successful';
    } catch (error) {
      throw new Error(`Failed to connect to API: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // SendGrid email test
  async testSendGrid(): Promise<any> {
    try {
      const response = await this.client.get('/api/test-sendgrid');
      return response.data;
    } catch (error) {
      throw new Error(`SendGrid test failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Send email via SendGrid
  async sendEmail(emailData: {
    to: string;
    subject: string;
    text: string;
    html?: string;
  }): Promise<any> {
    try {
      const response = await this.client.post('/api/sendgrid-email', emailData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to send email: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Practice Jobs API
  async getPracticeJobs(params: {
    query: 'browse-all' | 'filter-by-industry' | 'filter-by-level' | 'search-jobs';
    limit?: number;
    industry?: string;
    level?: string;
    search?: string;
  }): Promise<any> {
    try {
      const searchParams = new URLSearchParams();
      searchParams.append('query', params.query);
      
      if (params.limit) searchParams.append('limit', params.limit.toString());
      if (params.industry) searchParams.append('industry', params.industry);
      if (params.level) searchParams.append('level', params.level);
      if (params.search) searchParams.append('search', params.search);

      const response = await this.client.get(`/api/practice-jobs?${searchParams.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch practice jobs: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

export const apiClient = new ApiClient();